import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./TidelinePaywall.module.scss"

// ---------------------------------------------------------------------------
// "Tideline" (`/tideline`) — a self-contained EXPIRATION / win-back paywall for
// Private Photo Vault. (This run's type was drawn at random → Expiration.)
//
// Concept: Premium's automatic protection is a TIDE. Your memories rest along a
// shore. When Premium lapses the tide goes OUT — and the whole thesis is what
// that does NOT do: nothing washes away. Every photo and video is still sitting
// right there on the sand, fully intact. Bring the tide back IN (drag it up) and
// it laps up the beach, and one by one each memory comes back under Premium's
// protection. Yearly keeps the tide at high water across the entire year ahead;
// monthly and it slips straight back out.
//
// Divergence (vs the saturated expiration family, which is almost entirely
// warm-ivory + photographic + "gather/seal the year" + reveal-by-light):
//   - Layout:      a horizontal COASTAL SHORELINE + a data-viz TIDE/coverage
//                  chart. Not a card, calendar, road, arch, wheel, deck, console.
//   - Interaction: DRAG THE TIDE IN — a vertical tide-return pull. Not a
//                  fill/pour/seal/lever/stitch/wind/stamp/flip.
//   - Register:    rhythmic-reassuring / "it always comes back, nothing lost."
//   - Art:         bright coastal daylight + data-viz hybrid (NOT ivory-paper,
//                  NOT dark-skeuomorphic). Brand blue = the protective water,
//                  warm sand = the exposed/paused shore.
//   - Value:       win-back framed as an inevitable return — the no-data-loss
//                  guarantee is the literal mechanic, not a footnote.
//   - Motion:      a living sea (the wave never stops); the tide climbing to meet
//                  each memory. Deliberately NOT a grey→colour bloom — the photos
//                  are full colour the entire time, because they were never lost.
//
// Honest / App Store compliant: your library is untouched; only premium
// *features* pause. Yearly is the featured, most-prominent price; monthly is
// smaller and secondary. No fake countdown / scarcity; savings are computed from
// the real debug prices. Renders on built-in debug data; photos reuse bundled
// public/byheart/*.jpg (no network egress).
// ---------------------------------------------------------------------------

const PUB = process.env.PUBLIC_URL ?? ""
const photo = (file: string) => `${PUB}/byheart/${file}.jpg`

// scene geometry (viewBox 0 0 360 300)
const VW = 360
const VH = 300
// tide level 0 (fully out / lapsed) → 1 (full high water / protected)
const LEVEL_OUT = 0.12
const LEVEL_IN = 0.98
const LEVEL_REST = 0.97 // where the tide settles after the teaching demo
// waterline y for a given level: level 1 → high (small y), level 0 → low (big y)
const Y_HIGH = 148
const Y_LOW = 300
const lineY = (level: number) => Y_LOW - (Y_LOW - Y_HIGH) * level
// a memory counts as protected once the tide has climbed to within this many px
// of its base — the water reaching its feet, not drowning it
const KEEP_GAP = 8

type PlanKey = "yearly" | "monthly"

// Your memories resting along the shore in a gentle arc — the outer ones lowest
// so the rising tide reaches them first, the middle one highest, so protection
// fills inward as the tide comes in and drives the "n of 5 protected" readout.
const MEMORIES = [
  { img: "m03", cx: 42, base: 166, w: 52, tilt: -5 },
  { img: "m06", cx: 108, base: 158, w: 56, tilt: 3 },
  { img: "m09", cx: 180, base: 152, w: 60, tilt: -2 },
  { img: "m12", cx: 252, base: 158, w: 56, tilt: 4 },
  { img: "m15", cx: 318, base: 166, w: 52, tilt: -4 },
]

const clamp01 = (v: number) => Math.max(0, Math.min(1, v))

function monthLabel(d: Date) {
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" })
}

function TidelinePaywall() {
  const { state, actions } = useAppState()
  const reduce = useReducedMotion()

  useEffect(() => {
    // Match the safe-area insets to the sky/sea so the screen reads as one shore.
    actions.setHeaderInsetColor("#dcefff")
    actions.setFooterInsetColor("#ffffff")
  }, [actions])

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : ""
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : ""
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : ""
  const savings = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 0
    return Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
  }, [yearlyProduct, monthlyProduct])
  const trialDays = yearlyProduct?.introPricing?.period ?? 0

  // "Covered through" dates — future-self framing on the coverage chart.
  const coveredThrough = useMemo(() => {
    const now = new Date()
    const yr = new Date(now)
    yr.setFullYear(now.getFullYear() + 1)
    const mo = new Date(now)
    mo.setMonth(now.getMonth() + 1)
    return { yearly: monthLabel(yr), monthly: monthLabel(mo) }
  }, [])

  // ── tide state ────────────────────────────────────────────────────────────
  const [level, setLevel] = useState(LEVEL_OUT)
  const [eased, setEased] = useState(true) // eased transition during auto-demo
  const [dragging, setDragging] = useState(false)
  const [touched, setTouched] = useState(false)
  const [plan, setPlan] = useState<PlanKey>("yearly")

  const svgRef = useRef<SVGSVGElement>(null)
  const dragRef = useRef(false)
  const timers = useRef<number[]>([])

  const clearTimers = () => {
    timers.current.forEach((t) => window.clearTimeout(t))
    timers.current = []
  }

  // Teach the payoff once: it comes in from a low, lapsed tide up to high water,
  // then rests just below full so the gesture still has somewhere to go.
  useEffect(() => {
    if (reduce) {
      setEased(false)
      setLevel(LEVEL_IN)
      return
    }
    const t1 = window.setTimeout(() => setLevel(LEVEL_IN), 650)
    const t2 = window.setTimeout(() => setLevel(LEVEL_REST), 2500)
    const t3 = window.setTimeout(() => setEased(false), 3200)
    timers.current.push(t1, t2, t3)
    return clearTimers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setFromClientY = (clientY: number) => {
    const el = svgRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const y = ((clientY - r.top) / r.height) * VH
    // invert: dragging up (small y) raises the tide
    setLevel(clamp01((Y_LOW - y) / (Y_LOW - Y_HIGH)))
  }

  const onDown = (e: React.PointerEvent) => {
    e.preventDefault()
    clearTimers()
    setEased(false)
    setDragging(true)
    setTouched(true)
    dragRef.current = true
    try {
      svgRef.current?.setPointerCapture(e.pointerId)
    } catch {}
    setFromClientY(e.clientY)
  }
  const onMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return
    setFromClientY(e.clientY)
  }
  const onUp = (e: React.PointerEvent) => {
    dragRef.current = false
    setDragging(false)
    try {
      svgRef.current?.releasePointerCapture(e.pointerId)
    } catch {}
  }

  const wY = lineY(level)
  // a memory is protected once the tide has climbed to within KEEP_GAP of its base
  const keptFlags = MEMORIES.map((m) => wY <= m.base + KEEP_GAP)
  const keptCount = keptFlags.filter(Boolean).length
  const allKept = keptCount === MEMORIES.length
  const protectedNow = level > 0.55

  const handleClose = () => native.postNativeEvent("close")
  const buy = () => {
    const p = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (p) native.subscribe(p)
  }

  const waterMove = eased ? styles.waterEased : ""
  // coverage band fraction for the mini-chart under the plans
  const coverFrac = plan === "yearly" ? 1 : 1 / 12
  const coverLabel =
    plan === "yearly" ? coveredThrough.yearly : coveredThrough.monthly

  return (
    <div className={styles.page}>
      {/* chrome ------------------------------------------------------------- */}
      <header className={styles.top}>
        <button
          className={styles.close}
          onClick={handleClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="15" height="15">
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <span className={styles.brand}>
          Private Photo Vault <b>Premium</b>
        </span>
        <span
          className={`${styles.statusChip} ${protectedNow ? styles.chipOn : ""}`}
        >
          <span className={styles.chipDot} />
          {protectedNow ? "Protected" : "Paused"}
        </span>
      </header>

      {/* the shore ---------------------------------------------------------- */}
      <div className={styles.sceneWrap}>
        <svg
          ref={svgRef}
          className={styles.scene}
          viewBox={`0 0 ${VW} ${VH}`}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerLeave={onUp}
          onPointerCancel={onUp}
        >
          <defs>
            <linearGradient id="tlSky" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#eaf6ff" />
              <stop offset="62%" stopColor="#f4f9fe" />
              <stop offset="100%" stopColor="#fdf4e6" />
            </linearGradient>
            <linearGradient id="tlSand" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#fbeccf" />
              <stop offset="100%" stopColor="#f4dcae" />
            </linearGradient>
            <linearGradient id="tlWater" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#5b8dff" />
              <stop offset="45%" stopColor="#2f63f4" />
              <stop offset="100%" stopColor="#1c47c9" />
            </linearGradient>
            <linearGradient id="tlWaterBack" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#8fb4ff" />
              <stop offset="100%" stopColor="#3f6fe6" />
            </linearGradient>
            <radialGradient id="tlSun" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#fff6df" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#fff6df" stopOpacity="0" />
            </radialGradient>
            {MEMORIES.map((m, i) => (
              <clipPath id={`tlm${i}`} key={i}>
                <rect
                  x={m.cx - m.w / 2 + 4}
                  y={m.base - m.w + 4}
                  width={m.w - 8}
                  height={m.w - 12}
                  rx={7}
                />
              </clipPath>
            ))}
          </defs>

          {/* sky + soft sun */}
          <rect x="0" y="0" width={VW} height={VH} fill="url(#tlSky)" />
          <circle cx="300" cy="42" r="72" fill="url(#tlSun)" />

          {/* wet-sand beach the tide runs over */}
          <rect x="0" y={122} width={VW} height={VH} fill="url(#tlSand)" />
          {/* faint high-water mark line the tide can reach */}
          <line
            x1="0"
            y1={Y_HIGH}
            x2={VW}
            y2={Y_HIGH}
            className={styles.waterMark}
          />

          {/* the sea — vertical position is the live tide level, the surface
              wave animates horizontally so the water is always alive */}
          <g
            className={`${styles.water} ${waterMove}`}
            style={{ transform: `translateY(${wY}px)` }}
          >
            {/* back swell */}
            <g className={styles.swellBack}>
              <path
                d="M-360 14 q45 -14 90 0 t90 0 t90 0 t90 0 t90 0 t90 0 t90 0 t90 0 V320 H-360 Z"
                fill="url(#tlWaterBack)"
                opacity="0.55"
              />
            </g>
            {/* front surface */}
            <g className={styles.swellFront}>
              <path
                d="M-360 6 q30 12 60 0 t60 0 t60 0 t60 0 t60 0 t60 0 t60 0 t60 0 t60 0 t60 0 t60 0 t60 0 V320 H-360 Z"
                fill="url(#tlWater)"
              />
            </g>
            {/* glint on the surface */}
            <g className={styles.swellFront}>
              <path
                d="M-360 6 q30 12 60 0 t60 0 t60 0 t60 0 t60 0 t60 0 t60 0 t60 0 t60 0 t60 0 t60 0 t60 0"
                className={styles.glint}
              />
            </g>
          </g>

          {/* memories along the shore */}
          {MEMORIES.map((m, i) => {
            const kept = keptFlags[i]
            const top = m.base - m.w
            return (
              <g
                key={m.img}
                className={`${styles.mem} ${kept ? styles.memKept : ""}`}
                style={{ transformOrigin: `${m.cx}px ${m.base - m.w / 2}px` }}
              >
                {/* protective halo when the tide has reached it */}
                <rect
                  x={m.cx - m.w / 2 - 3}
                  y={top - 3}
                  width={m.w + 6}
                  height={m.w - 4}
                  rx={11}
                  className={styles.memHalo}
                />
                <g transform={`rotate(${m.tilt} ${m.cx} ${m.base - m.w / 2})`}>
                  {/* white frame */}
                  <rect
                    x={m.cx - m.w / 2}
                    y={top}
                    width={m.w}
                    height={m.w - 8}
                    rx={9}
                    className={styles.memFrame}
                  />
                  <image
                    href={photo(m.img)}
                    x={m.cx - m.w / 2 + 4}
                    y={top + 4}
                    width={m.w - 8}
                    height={m.w - 12}
                    clipPath={`url(#tlm${i})`}
                    preserveAspectRatio="xMidYMid slice"
                  />
                  {/* state badge: kept (lock) vs resting (dot) */}
                  <g
                    transform={`translate(${m.cx + m.w / 2 - 11} ${top + 3})`}
                    className={styles.badge}
                  >
                    <circle r="8.5" className={styles.badgeBg} />
                    {kept ? (
                      <path
                        d="M-2.7 -0.4a2.7 2.7 0 0 1 5.4 0 M-3.6 -0.4h7.2v5h-7.2z"
                        className={styles.badgeLock}
                        transform="translate(0 0.3)"
                      />
                    ) : (
                      <circle r="2.4" className={styles.badgeDot} />
                    )}
                  </g>
                </g>
              </g>
            )
          })}

          {/* drag affordance — a grip tab hugging the right edge, riding the
              waterline; the whole sea is draggable, this just shows how */}
          <g
            className={`${styles.handle} ${waterMove}`}
            style={{ transform: `translate(${VW - 15}px, ${wY}px)` }}
          >
            <rect
              x={-15}
              y={-21}
              width={30}
              height={42}
              rx={11}
              className={styles.handlePill}
            />
            <path
              d="M-4 -8 L0 -13 L4 -8 M-4 8 L0 13 L4 8"
              className={styles.handleArrows}
            />
            <path d="M-5 -1 h10 M-5 3 h10" className={styles.handleGrip} />
          </g>
        </svg>

        {/* live readout + hint (crisp HTML over the scene) */}
        <div className={styles.readout}>
          <span className={styles.readCount}>
            {keptCount}
            <span className={styles.readOf}> / {MEMORIES.length}</span>
          </span>
          <span className={styles.readText}>
            {allKept
              ? "memories under Premium's protection"
              : keptCount === 0
                ? "the tide is out — protection paused"
                : "memories the tide has reached"}
          </span>
        </div>
        {!touched && (
          <div className={`${styles.hint} ${allKept ? styles.hintFade : ""}`}>
            <span>Drag the tide back in</span>
          </div>
        )}
      </div>

      {/* the pitch ---------------------------------------------------------- */}
      <section className={styles.pitch}>
        <h1>
          Your protection went out <em>with the tide.</em>
        </h1>
        <p className={styles.reassure}>
          Nothing washed away. Every photo and video is still right here in your
          vault — the tide only decides whether Premium is actively guarding
          them. Bring it back in and it picks up exactly where it left off,
          <b> from today.</b>
        </p>
      </section>

      {/* what the tide brings back ----------------------------------------- */}
      <div className={styles.brings}>
        {[
          ["Auto-backup resumes", "Every new memory saved in full quality"],
          ["Locks re-arm", "Face-down lock, decoy & pattern lock switch on"],
          ["In sync everywhere", "Your library, matched across your devices"],
        ].map(([t, s]) => (
          <div className={styles.bring} key={t}>
            <span className={styles.bringWave} aria-hidden>
              <svg viewBox="0 0 26 14" width="26" height="14">
                <path
                  d="M1 8 q3 -6 6.5 0 t6.5 0 t6.5 0 t6.5 0"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <div className={styles.bringText}>
              <b>{t}</b>
              <span>{s}</span>
            </div>
          </div>
        ))}
      </div>

      {/* the offer ---------------------------------------------------------- */}
      <div className={styles.offer}>
        {/* coverage chart — how far the tide stays in, per plan */}
        <div className={styles.coverChart}>
          <div className={styles.coverHead}>
            <span>Protection stays in through</span>
            <b className={styles.coverDate}>{coverLabel}</b>
          </div>
          <div className={styles.coverTrack}>
            <div
              className={styles.coverFill}
              style={{ width: `${coverFrac * 100}%` }}
            >
              <span className={styles.coverFillWave} aria-hidden />
            </div>
            <span className={styles.coverNow}>Today</span>
            <span className={styles.coverEnd}>
              {plan === "yearly" ? "+12 months" : "+1 month"}
            </span>
          </div>
        </div>

        <button
          className={`${styles.planYear} ${plan === "yearly" ? styles.planOn : ""}`}
          onClick={() => setPlan("yearly")}
        >
          {savings > 0 && (
            <span className={styles.bestTag}>SAVE {savings}%</span>
          )}
          <div className={styles.planYearMain}>
            <div className={styles.planYearL}>
              <span className={styles.planName}>Yearly</span>
              <span className={styles.planSub}>
                Keeps the tide in all year long
              </span>
            </div>
            <div className={styles.planYearR}>
              <b className={styles.planPrice}>{yearlyPrice}</b>
              <span className={styles.planPer}>/year</span>
            </div>
          </div>
          <div className={styles.planYearFoot}>
            just {yearlyPerMonth}/mo · billed once a year
          </div>
        </button>

        <button
          className={`${styles.planMonth} ${plan === "monthly" ? styles.planOn : ""}`}
          onClick={() => setPlan("monthly")}
        >
          <span className={styles.planName}>Monthly</span>
          <span className={styles.planMonthNote}>
            goes back out every month
          </span>
          <span className={styles.planMonthPrice}>
            {monthlyPrice}
            <span className={styles.planPer}>/mo</span>
          </span>
        </button>

        <button className={styles.cta} onClick={buy}>
          <span className={styles.ctaTop}>Bring the tide back in</span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `${trialDays ? `${trialDays}-day free trial, then ` : ""}${yearlyPrice}/yr · cancel anytime`
              : `${monthlyPrice}/mo · cancel anytime`}
          </span>
        </button>

        <div className={styles.legal}>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
          <span>·</span>
          <span className={styles.legalNote}>Sample memories shown</span>
        </div>
      </div>
    </div>
  )
}

export default TidelinePaywall
